'use client'

import * as React from 'react'
import { BaseDrawer, type BaseDrawerProps } from './base-drawer'
import { LoadingButton } from './loading-button'
import { Button } from './button'
import { Alert, AlertDescription } from './alert'
import { Spinner } from './spinner'
import { cn } from '../../lib/utils'
import { X, Save, AlertCircle } from 'lucide-react'

export interface FormDrawerProps extends Omit<BaseDrawerProps, 'footerContent'> {
  /** Whether the form is currently submitting */
  isSubmitting?: boolean
  /** Whether the form data is loading (for edit mode) */
  isLoading?: boolean
  /** Error message to display (prevents drawer close on error) */
  error?: string | null
  /** Callback to clear the error */
  onClearError?: () => void
  /** Callback when save is clicked */
  onSave?: () => void
  /** Callback when cancel is clicked (defaults to onOpenChange(false)) */
  onCancel?: () => void
  /** Save button text */
  saveText?: string
  /** Saving button text (while submitting) */
  savingText?: string
  /** Cancel button text */
  cancelText?: string
  /** Whether to show the cancel button */
  showCancel?: boolean
  /** Whether to show the save button */
  showSave?: boolean
  /** Custom footer content (replaces default buttons) */
  customFooter?: React.ReactNode
  /** Additional footer actions (prepended to default buttons) */
  additionalActions?: React.ReactNode
  /** Whether the save button should be disabled */
  saveDisabled?: boolean
  /** Form submit handler - alternative to onSave for native form submission */
  onSubmit?: (e: React.FormEvent) => void
}

/**
 * FormDrawer - BaseDrawer wrapper optimized for forms with CRUD operations
 *
 * Features:
 * - Loading overlay when fetching data (edit mode)
 * - Submitting state with disabled form and loading buttons
 * - Error display that prevents drawer from closing
 * - Smart cancel behavior (prevents data loss)
 * - RTL support
 *
 * Key Behavior:
 * - When error is set, drawer will NOT close on background click
 * - Error must be cleared before drawer can close
 * - Loading state shows spinner overlay
 * - Submitting state disables all inputs and buttons
 *
 * @example
 * // Basic usage
 * <FormDrawer
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   isSubmitting={isSubmitting}
 *   error={error}
 *   onSave={handleSubmit}
 *   title="Add Address"
 * >
 *   <form>...</form>
 * </FormDrawer>
 *
 * @example
 * // Edit mode with loading
 * <FormDrawer
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   isLoading={isLoadingAddress}
 *   isSubmitting={isSubmitting}
 *   error={error}
 *   onSave={handleUpdate}
 *   title="Edit Address"
 * >
 *   <form>...</form>
 * </FormDrawer>
 */
function FormDrawer({
  children,
  open,
  onOpenChange,
  isSubmitting = false,
  isLoading = false,
  error,
  onClearError,
  onSave,
  onCancel,
  saveText = 'Save',
  savingText = 'Saving...',
  cancelText = 'Cancel',
  showCancel = true,
  showSave = true,
  customFooter,
  additionalActions,
  saveDisabled = false,
  onSubmit,
  isRTL = false,
  ...props
}: FormDrawerProps) {
  // Handle drawer close - prevent if error exists
  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      // If trying to close and there's an error, don't close
      if (!newOpen && error) {
        return
      }
      // If trying to close while submitting, don't close
      if (!newOpen && isSubmitting) {
        return
      }
      onOpenChange(newOpen)
    },
    [error, isSubmitting, onOpenChange]
  )

  // Handle cancel button click
  const handleCancel = React.useCallback(() => {
    if (onCancel) {
      onCancel()
    } else {
      // Clear error first if exists
      if (error && onClearError) {
        onClearError()
      }
      onOpenChange(false)
    }
  }, [error, onCancel, onClearError, onOpenChange])

  // Handle save button click
  const handleSave = React.useCallback(() => {
    // Clear previous error before attempting save
    if (onClearError) {
      onClearError()
    }
    if (onSave) {
      onSave()
    }
  }, [onClearError, onSave])

  // Check RTL from prop or document direction
  const effectiveRTL = isRTL || (typeof document !== 'undefined' && document.documentElement.dir === 'rtl')

  // Footer content with error display and action buttons
  const footerContent = customFooter || (
    <div className="space-y-3">
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className={cn(
            effectiveRTL && 'text-right'
          )}>
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        {/* Additional actions */}
        {additionalActions}

        {/* Cancel Button */}
        {showCancel && (
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-12 rounded-xl"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <X className={cn('w-4 h-4', effectiveRTL ? 'ms-2' : 'me-2')} />
            {cancelText}
          </Button>
        )}

        {/* Save Button */}
        {showSave && (
          <LoadingButton
            type={onSubmit ? 'submit' : 'button'}
            className="flex-1 h-12 rounded-xl"
            onClick={onSubmit ? undefined : handleSave}
            loading={isSubmitting}
            loadingText={savingText}
            disabled={saveDisabled || isLoading}
          >
            <Save className={cn('w-4 h-4', effectiveRTL ? 'ms-2' : 'me-2')} />
            {saveText}
          </LoadingButton>
        )}
      </div>
    </div>
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={handleOpenChange}
      footerContent={footerContent}
      isRTL={isRTL}
      {...props}
    >
      {/* Loading Overlay (for edit mode data fetching) */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-3xl">
          <div className="flex flex-col items-center gap-3">
            <Spinner className="h-8 w-8" />
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        </div>
      )}

      {/* Submitting Overlay (subtle, doesn't block view) */}
      {isSubmitting && (
        <div className="absolute inset-0 bg-background/50 z-40 pointer-events-auto cursor-wait rounded-3xl" />
      )}

      {/* Form Content */}
      {onSubmit ? (
        <form onSubmit={onSubmit} className="contents">
          {children}
        </form>
      ) : (
        children
      )}
    </BaseDrawer>
  )
}

export { FormDrawer }
